import React, { useMemo, useRef, useState, useEffect } from "react";
import "./MultiSelect.css";

type Item = { id: string; name: string };

const normalize = (s: string) =>
    s.normalize("NFD")
        .toLowerCase()
        .trim();

const useDebounced = (value: string, delay = 150) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

interface MultiSelectProps {
    label: string;
    items: Item[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    label,
    items,
    selectedIds,
    onChange,
    placeholder,
    disabled,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounced(query);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const filtered = useMemo(() => {
        const q = normalize(debouncedQuery);
        if (!q) return items;
        return items.filter((i) => normalize(i.name).includes(q));
    }, [items, debouncedQuery]);

    const [visible, setVisible] = useState(100);
    useEffect(() => setVisible(100), [debouncedQuery]);
    const toRender = filtered.slice(0, visible);

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (
                open &&
                !btnRef.current?.contains(e.target as Node) &&
                !listRef.current?.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const toggleId = (id: string) => {
        onChange(
            selectedIds.includes(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id]
        );
    };

    const selectAllFiltered = () => {
        const ids = filtered.map((i) => i.id);
        const next = new Set([...selectedIds, ...ids]);
        onChange(Array.from(next));
    };

    const clearAllFiltered = () => {
        const remove = new Set(filtered.map((i) => i.id));
        onChange(selectedIds.filter((id) => !remove.has(id)));
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, toRender.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const item = toRender[activeIndex];
            if (item) toggleId(item.id);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const selectedCount = selectedIds.length;

    return (
        <div className="ms-wrapper">
            <div className="ms-label-row">
                <label className="ms-label">{label}</label>
                <span className="ms-badge">{selectedCount} sélectionné(s)</span>
            </div>

            {selectedCount > 0 && (
                <div className="ms-chips">
                    {selectedIds
                        .map((id) => items.find((i) => i.id === id))
                        .filter(Boolean)
                        .map((i) => (
                            <span key={i!.id} className="ms-chip" title={i!.name}>
                                <span className="ms-chip-text">{i!.name}</span>
                                <button
                                    type="button"
                                    className="ms-chip-x"
                                    onClick={() => toggleId(i!.id)}
                                    aria-label={`Retirer ${i!.name}`}
                                    disabled={disabled}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                </div>
            )}

            <button
                ref={btnRef}
                type="button"
                className={`ms-trigger ${open ? "open" : ""}`}
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selectedCount > 0
                    ? `${selectedCount} sélectionné(s)`
                    : placeholder || "Sélectionner..."}
                <span className="ms-caret" aria-hidden>
                    ▾
                </span>
            </button>

            {open && (
                <div
                    ref={listRef}
                    className="ms-menu"
                    role="listbox"
                    tabIndex={-1}
                    onKeyDown={onKeyDown}
                >
                    <div className="ms-search-row">
                        <input
                            className="ms-search"
                            placeholder="Rechercher…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoFocus
                        />
                        <div className="ms-menu-actions">
                            <button type="button" onClick={selectAllFiltered}>
                                Tout sélectionner
                            </button>
                            <button type="button" onClick={clearAllFiltered}>
                                Tout désélectionner
                            </button>
                        </div>
                    </div>

                    <div
                        className="ms-list"
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
                                setVisible((v) => Math.min(v + 200, filtered.length));
                            }
                        }}
                    >
                        {toRender.length === 0 ? (
                            <div className="ms-empty">Aucun résultat</div>
                        ) : (
                            toRender.map((i, idx) => {
                                const checked = selectedIds.includes(i.id);
                                return (
                                    <label
                                        key={i.id}
                                        className={`ms-item ${idx === activeIndex ? "active" : ""}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleId(i.id)}
                                        />
                                        <span className="ms-item-text">{i.name}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelect;
